<script setup>
  import { ref, onMounted, watch } from "vue";
  import { RouterLink, useRoute } from "vue-router";
  import { UserPlus, X } from "lucide-vue-next";
  import { useStore } from "vuex";

  // Vuex store and route access
  const store = useStore();
  const route = useRoute();
  const visible = ref(true);
  const goClickCount = ref(0); // 🧮 Track number of times clicked

  // Emit event to parent
  const emit = defineEmits(["goClicked"]);

  // Fetch total students on mount
  onMounted(async () => {
    await store.dispatch("fetchTotalTeacherCountOnboarding");
  });

  // Hide banner if students exist
  watch(
    () => store.state.onboardingTotalTeachers,
    (newVal) => {
      if (newVal >= 1) visible.value = false;
    },
    { immediate: true }
  );

  // Close banner manually
  function close() {
    visible.value = false;
  }

  // Handle "Go" button click
  function handleGoClick(event) {
    goClickCount.value++; // increase count
    if (route.path === "/students") {
      // Prevent redundant navigation
      event.preventDefault();
      emit("goClicked", { clicked: true, count: goClickCount.value });
    } else {
      emit("goClicked", { clicked: false, count: goClickCount.value });
    }
  }
</script>

<template>
  <!-- Global top banner -->
  <transition
    name="banner-slide"
    appear
  >
    <div
      v-if="visible"
      class="fixed top-16 sm:top-20 z-50 left-0 right-0 w-full flex justify-center px-3 sm:px-4 pointer-events-none"
    >
      <div
        class="pointer-events-auto w-full max-w-xl flex items-center justify-between gap-3 rounded-2xl shadow-xl border border-indigo-500/30 bg-slate-900/95 dark:bg-[#111C30]/95 backdrop-blur-xl text-white px-4 py-3 animate-fade-in"
        role="region"
        aria-label="Onboarding banner"
      >
        <!-- Left: Icon + Text -->
        <div class="flex items-center gap-3 min-w-0 flex-1">
          <div class="w-8 h-8 rounded-xl bg-indigo-500/20 text-indigo-400 flex items-center justify-center shrink-0 border border-indigo-500/30">
            <UserPlus class="w-4 h-4" />
          </div>
          <p class="text-xs sm:text-sm font-semibold text-slate-100 truncate sm:whitespace-normal">
            Add your teaching faculty to manage classes and attendance!
          </p>
        </div>

        <!-- Right: Buttons -->
        <div class="flex items-center gap-2 shrink-0">
          <RouterLink
            @click="handleGoClick"
            to="/teacher"
            class="inline-flex items-center rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white px-3 py-1.5 text-xs font-bold shadow-xs transition-all whitespace-nowrap active:scale-95"
          >
            Add Faculty &rarr;
          </RouterLink>

          <button
            type="button"
            @click="close"
            class="rounded-lg p-1 text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
            aria-label="Dismiss banner"
          >
            <X class="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<style scoped>
  /* 🌈 Slide-in Animation */
  .banner-slide-enter-active,
  .banner-slide-leave-active {
    transition: all 0.4s ease;
  }
  .banner-slide-enter-from {
    opacity: 0;
    transform: translateY(-20px);
  }
  .banner-slide-leave-to {
    opacity: 0;
    transform: translateY(-20px);
  }

  /* 🔁 Animated gradient */
  @keyframes gradient-x {
    0%,
    100% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
  }
  .animate-gradient-x {
    background-size: 200% 200%;
    animation: gradient-x 6s ease infinite;
  }

  /* 🫧 Slow bounce for icon */
  @keyframes bounce-slow {
    0%,
    100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-3px);
    }
  }
  .animate-bounce-slow {
    animation: bounce-slow 2s infinite;
  }
</style>
